import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Popover,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { EditOutlined, EyeOutlined, SwitcherOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import App from './LayoutMobile';
import { Config } from './Common/Config';

const { Title } = Typography;

const DATE_DISPLAY_FORMAT = 'DD MMM YYYY';
const DATE_STORAGE_FORMAT = 'YYYY-MM-DD';

const BankHoliday = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [messageApi, contextHolder] = message.useMessage();

  // Get user role from localStorage
  const getUserRole = () => {
    let userTempData = localStorage.getItem('userData');
    let userData = {};
    if (userTempData) {
      userData = JSON.parse(userTempData);
    }
    return userData?.roles?.[0]?.role_id;
  };

  const userRole = getUserRole();
  const isAdmin = userRole === 1;

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const normalizeStatus = (value) => {
    if (typeof value === 'boolean') {
      return value ? 'Active' : 'Inactive';
    }
    if (typeof value === 'number') {
      return value === 1 ? 'Active' : 'Inactive';
    }
    if (typeof value === 'string') {
      const normalized = value.toLowerCase();
      return normalized === 'active' || normalized === '1' || normalized === 'true'
        ? 'Active'
        : 'Inactive';
    }
    return 'Inactive';
  };

  const mapHolidayItem = (item, index) => ({
    key: `${item?.id || item?.bank_holiday_id || index}`,
    id: item?.id || item?.bank_holiday_id || null,
    name: item?.name || item?.holiday_name || '-',
    date: item?.date || item?.holiday_date || item?.created_at || '',
    status: normalizeStatus(item?.is_active ?? item?.status),
    description: item?.description || '',
  });

  const getAllBankHolidays = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${Config.url}bank-holidays`, {
        headers: getAuthHeaders(),
      });

      const responseData = response?.data?.data;
      const holidayList = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
          ? responseData.data
          : [];

      setHolidays(holidayList.map(mapHolidayItem));
    } catch (error) {
      messageApi.error({
        type: 'error',
        content:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to fetch bank holidays',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllBankHolidays();
  }, []);

  const showModal = () => {
    setModalMode('add');
    setEditingHoliday(null);
    form.setFieldsValue({ status: true });
    setIsModalOpen(true);
  };

  const showEditModal = (record) => {
    setModalMode('edit');
    setEditingHoliday(record);
    form.setFieldsValue({
      holidayName: record?.name || '',
      date: record?.date ? dayjs(record.date) : null,
      status: record?.status === 'Active',
      description: record?.description || '',
    });
    setIsModalOpen(true);
  };

  const showViewModal = (record) => {
    setModalMode('view');
    setEditingHoliday(record);
    form.setFieldsValue({
      holidayName: record?.name || '',
      date: record?.date ? dayjs(record.date) : null,
      status: record?.status === 'Active',
      description: record?.description || '',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    form.resetFields();
    setEditingHoliday(null);
    setModalMode('add');
    setIsModalOpen(false);
  };

  const handleStatusToggle = async (record, checked) => {
    if (!record?.id) {
      return;
    }

    const previousStatus = record.status;
    const nextStatus = checked ? 'Active' : 'Inactive';

    setHolidays((prev) =>
      prev.map((item) =>
        item.key === record.key ? { ...item, status: nextStatus } : item,
      ),
    );

    try {
      await axios.put(
        `${Config.url}bank-holidays/${record.id}`,
        {
          is_active: checked,
        },
        {
          headers: getAuthHeaders(),
        },
      );

      messageApi.success({
        type: 'success',
        content: 'Holiday status updated',
      });
    } catch (error) {
      setHolidays((prev) =>
        prev.map((item) =>
          item.key === record.key ? { ...item, status: previousStatus } : item,
        ),
      );

      messageApi.error({
        type: 'error',
        content:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update holiday status',
      });
    }
  };

  const handleSave = async () => {
    if (modalMode === 'view') {
      closeModal();
      return;
    }

    try {
      const values = await form.validateFields();
      setIsSaving(true);

      if (editingHoliday?.id) {
        await axios.put(
          `${Config.url}bank-holidays/${editingHoliday.id}`,
          {
            name: values.holidayName.trim(),
            is_active: !!values.status,
          },
          {
            headers: getAuthHeaders(),
          },
        );

        messageApi.success({
          type: 'success',
          content: 'Bank holiday updated successfully',
        });
      } else {
        const payload = {
          name: values.holidayName.trim(),
          holiday_date: values.date.format(DATE_STORAGE_FORMAT),
          description: values.description?.trim() || '',
        };

        await axios.post(`${Config.url}bank-holidays`, payload, {
          headers: getAuthHeaders(),
        });

        messageApi.success({
          type: 'success',
          content: 'Bank holiday added successfully',
        });
      }

      await getAllBankHolidays();
      closeModal();
    } catch (error) {
      // antd handles field-level validation errors automatically
      if (error?.response || error?.message) {
        messageApi.error({
          type: 'error',
          content:
            error?.response?.data?.message ||
            error?.message ||
            `Failed to ${editingHoliday?.id ? 'update' : 'add'} bank holiday`,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const actionContent = (record) => (
    <>
      <a
        onClick={() => showViewModal(record)}
        style={{ color: '#008385' }}
      >
        <EyeOutlined style={{ color: '#008385', fontSize: '18px', marginBottom: 10 }} /> View
      </a>
      {isAdmin && (
        <>
          <br />
          <a
            onClick={() => showEditModal(record)}
            style={{ color: '#008385' }}
          >
            <EditOutlined style={{ color: '#008385', fontSize: '18px', marginBottom: 10 }} /> Edit
          </a>
          <br />
          <a
            onClick={() => handleStatusToggle(record, record.status !== 'Active')}
            style={{ color: '#008385' }}
          >
            <SwitcherOutlined style={{ color: '#008385', fontSize: '18px' }} /> {record.status === 'Active' ? 'Make Inactive' : 'Make Active'}
          </a>
        </>
      )}
    </>
  );

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        render: (date) => dayjs(date).format(DATE_DISPLAY_FORMAT),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => (
        //   <Tag color={status === 'Active' ? 'green' : 'default'}>{status}</Tag>
       <span>{status}</span>
        ),
      },
      {
        title: '',
        key: 'action',
        render: (_, record) => (
          <div style={{ textAlign: 'right' }}>
            <Popover placement="left" content={() => actionContent(record)} title="Action">
              ...
            </Popover>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <App>
      {contextHolder}
      <div style={{ padding: 24, background: '#fff' }}>
        <div
          style={{
            width: '100%',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ minWidth: 120 }}>
            <Button onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>

          <div style={{ flex: 1, textAlign: 'center' }}>
            <Title level={4} style={{ margin: 0 }}>
              Bank Holidays
            </Title>
          </div>

          <div style={{ minWidth: 120, textAlign: 'right' }}>
            <Button
              type="primary"
              onClick={showModal}
              disabled={!isAdmin}
              title={!isAdmin ? 'Only admins can add holidays' : ''}
            >
              Add New Holiday
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin className="custom-spinner" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={holidays}
            rowKey="key"
            pagination={{ pageSize: 20 }}
          />
        )}

        <Modal
          title={
            modalMode === 'view'
              ? 'View Holiday'
              : editingHoliday
                ? 'Edit Holiday'
                : 'Add New Holiday'
          }
          open={isModalOpen}
          onOk={handleSave}
          onCancel={closeModal}
          okText={modalMode === 'view' ? 'Close' : 'Save'}
          cancelText="Cancel"
          okButtonProps={{ style: { display: modalMode === 'view' ? 'none' : undefined } }}
          confirmLoading={isSaving}
          destroyOnClose
        >
          <ConfigProvider
            theme={{
              components: {
                Switch: {
                  colorPrimary: '#008385',
                  colorPrimaryHover: '#008385',
                },
              },
            }}
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={{ status: true }}
            >
              <Form.Item
                label="Holiday Name"
                name="holidayName"
                rules={[
                  { required: true, message: 'Please enter holiday name' },
                  { whitespace: true, message: 'Holiday name cannot be empty' },
                ]}
              >
                <Input placeholder="Enter holiday name" disabled={modalMode === 'view'} />
              </Form.Item>

              <Form.Item
                label="Date"
                name="date"
                rules={[{ required: !editingHoliday, message: 'Please select date' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format={DATE_DISPLAY_FORMAT}
                  disabled={!!editingHoliday || modalMode === 'view'}
                />
              </Form.Item>

              <Form.Item
                label="Status"
                name="status"
                valuePropName="checked"
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={modalMode === 'view'} />
              </Form.Item>

              <Form.Item
                label="Description"
                name="description"
              >
                <Input.TextArea
                  placeholder="Optional details here"
                  rows={3}
                  disabled={modalMode === 'view'}
                />
              </Form.Item>
            </Form>
          </ConfigProvider>
        </Modal>
      </div>
    </App>
  );
};

export default BankHoliday;
